"use client";

import { useEffect, useRef } from "react";
import { CONTINENT, WORLD_GRID, WORLD_H, WORLD_MARKS, WORLD_W } from "@/lib/worldGrid";

type Point = { lon: number; lat: number; g: number; c: number };

/**
 * A slowly rotating dot globe with the countries we have users in lit gold, and
 * a ripple firing periodically from one of them.
 *
 * Drawn on a plain 2D canvas — no WebGL, no library, no external assets. The
 * sphere's radius and centre are derived from the container on every frame, so
 * it can never overflow its box at any viewport.
 */
export default function TrustGlobe({ className = "" }: { className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const cv = canvasRef.current;
    if (!host || !cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Build the point set once, in lon/lat.
    const pts: Point[] = [];
    for (let r = 0; r < WORLD_H; r++) {
      for (let c = 0; c < WORLD_W; c++) {
        const g = +WORLD_GRID[r][c];
        if (!g) continue;
        pts.push({
          lon: (c / WORLD_W) * Math.PI * 2 - Math.PI,
          lat: Math.PI / 2 - (r / WORLD_H) * Math.PI,
          g,
          c,
        });
      }
    }
    // An equirectangular grid bunches at the poles once wrapped onto a sphere;
    // thinning by 1/cos(lat) spaces the dots evenly instead.
    //
    // `coarse` halves the budget again on small screens: the full ~2700-point
    // field measured 15fps on an 8x-throttled CPU (a low-end phone), which is
    // visibly janky. At this size the dropped dots aren't perceptible.
    const coarse = window.innerWidth < 900;
    const globePts = pts.filter((p, i) => {
      if (coarse && i % 2) return false;
      return p.c % Math.max(1, Math.round(1 / Math.max(0.16, Math.cos(p.lat)))) === 0;
    });
    const marks = WORLD_MARKS.map(([c, r]) => ({
      lon: (c / WORLD_W) * Math.PI * 2 - Math.PI,
      lat: Math.PI / 2 - (r / WORLD_H) * Math.PI,
      c,
    }));

    let vw = 0;
    let vh = 0;
    let dpr = 1;
    const resize = () => {
      const b = host.getBoundingClientRect();
      vw = b.width;
      vh = b.height;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(vw * dpr);
      cv.height = Math.round(vh * dpr);
      cv.style.width = `${vw}px`;
      cv.style.height = `${vh}px`;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    const SWEEP = 1500;
    const LEAD = 1.18;
    const start = performance.now();
    let spin = 0;
    let last = start;
    let introP = reduce ? 1 : 0;
    const ignited = new Map<number, number>();
    let ripples: { lon: number; lat: number; t: number }[] = [];
    let raf = 0;

    const spawn = () => {
      const m = marks[Math.floor(Math.random() * marks.length)];
      ripples.push({ lon: m.lon, lat: m.lat, t: 0 });
      if (ripples.length > 6) ripples.shift();
    };
    const ripple = reduce ? 0 : window.setInterval(() => introP >= 1 && spawn(), 1500);

    const frame = (now: number) => {
      const t = now / 1000;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (introP < 1) introP = 1 - Math.pow(1 - Math.min(1, (now - start) / SWEEP), 3);
      if (!reduce) spin += dt * 0.17; // ~37s per revolution

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, vw, vh);
      if (!vw || !vh) {
        raf = requestAnimationFrame(frame);
        return;
      }

      // The sphere's right edge is aligned to the SAME container the copy uses
      // (max-w-6xl + px-10 / px-6), so the hero's left and right margins match.
      // Deriving it from an independent percentage left the globe 54px closer
      // to the edge than the headline was — visibly lopsided.
      const GLOWK = 1.2;
      const PAD = vw >= 640 ? 40 : 24;
      const contentW = Math.min(1152, vw);
      const contentRight = (vw - contentW) / 2 + contentW - PAD;
      const R = Math.min(vh * 0.38, (vw * 0.46) / 2);
      const CX = contentRight - R;
      const CY = vh * 0.5;
      const U = R / 300;
      const front = introP * LEAD;

      const grd = ctx.createRadialGradient(CX, CY, R * 0.7, CX, CY, R * GLOWK);
      grd.addColorStop(0, "rgba(120,190,220,0.10)");
      grd.addColorStop(1, "rgba(120,190,220,0)");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(CX, CY, R * GLOWK, 0, 7);
      ctx.fill();
      ctx.fillStyle = "rgba(12,20,34,0.55)";
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, 7);
      ctx.fill();
      ctx.strokeStyle = "rgba(150,200,225,0.13)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, 7);
      ctx.stroke();

      const proj = (lon: number, lat: number) => {
        const cl = Math.cos(lat);
        const L = lon + spin;
        return { x: CX + cl * Math.sin(L) * R, y: CY - Math.sin(lat) * R, z: cl * Math.cos(L) };
      };

      for (const p of globePts) {
        if (front < p.c / WORLD_W) continue; // not reached by the intro sweep yet
        const q = proj(p.lon, p.lat);
        if (q.z <= 0.02) continue; // far hemisphere
        const [cr, cg, cb] = CONTINENT[p.g] ?? [226, 234, 246];
        ctx.fillStyle = `rgba(${cr},${cg},${cb},${(0.3 + 0.62 * q.z).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(q.x, q.y, (1.9 + 1.9 * q.z) * U * 0.5, 0, 7);
        ctx.fill();
      }

      ctx.shadowColor = "#ffc23c";
      marks.forEach((m, i) => {
        if (front < m.c / WORLD_W) return;
        if (!ignited.has(i)) ignited.set(i, now);
        const q = proj(m.lon, m.lat);
        if (q.z <= 0.02) return;
        const age = Math.min(1, (now - (ignited.get(i) as number)) / 520);
        const pop = 1 + Math.sin(age * Math.PI) * 0.8;
        const ph = reduce ? 0 : Math.sin(t * 1.5 + i * 0.9) * 0.5 + 0.5;
        ctx.shadowBlur = (9 + ph * 11) * q.z * pop;
        ctx.fillStyle = `rgba(255,200,70,${(
          (0.72 + 0.28 * ph) *
          Math.min(1, q.z * 1.5) *
          Math.min(1, age * 1.6)
        ).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(q.x, q.y, (3.0 + ph * 1.1) * q.z * pop * U * 0.7, 0, 7);
        ctx.fill();
      });
      ctx.shadowBlur = 0;

      if (!reduce) {
        // one ripple = one signature landing on the transparency log
        ripples.forEach((rp) => {
          rp.t += dt;
          const q = proj(rp.lon, rp.lat);
          if (q.z <= 0.02) return;
          const a = Math.max(0, 1 - rp.t / 2.4);
          if (a <= 0) return;
          ctx.strokeStyle = `rgba(255,196,60,${(a * 0.6 * q.z).toFixed(3)})`;
          ctx.lineWidth = 1.1;
          ctx.beginPath();
          ctx.ellipse(q.x, q.y, rp.t * 30 * q.z, rp.t * 30 * q.z * 0.92, 0, 0, 7);
          ctx.stroke();
        });
        ripples = ripples.filter((r) => r.t < 2.4);
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      if (ripple) clearInterval(ripple);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={hostRef} className={className} aria-hidden="true">
      <canvas ref={canvasRef} className="block absolute inset-0" />
    </div>
  );
}
